import React from 'react';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { Icon } from '@blueprintjs/core';
import { RootState } from 'data/root.reducers';
import { AuditDetails, WithAuditDetails } from 'data/types';
import { UpdatePasswordInterface } from 'data/authentication/authentication.types';
import {
  AuditActionType,
  AuditService,
  getAuditDetails,
  getAuditFieldInfo,
} from 'shared/userAudit/audit-constants';
import { getDXSettingsAndReturn } from '../../xcipio/discovery-xcipio-actions';
import ss8logo from '../../assets/ss8-logo.png';
import { toPasswordPolicyDescription } from '../../policies/policies-util';
import './passwordupdate.scss';

const Login = '/authentication/login';

class PasswordUpdate extends React.Component<any, any> {
  state = {
    error: false,
    userFromLogin: '',
    passwordFromLogin: '',
    password: '',
    newpassword: '',
    confirmpassword: '',
    prePathname: '',
    passwordPolicy: '',
  };

  componentDidMount() {
    if (this.props.authentication.errorStatus === '489') {
      this.setState({
        userFromLogin: this.props.location.username,
        passwordPolicy: toPasswordPolicyDescription(this.props.securityPolicy.entities),
      });
    } else {
      this.setState({
        userFromLogin: this.props.location.username,
        prePathname: this.props.location.curpath,
        passwordPolicy: toPasswordPolicyDescription(this.props.securityPolicy.entities),
      });
    }
  }

  async componentDidUpdate(prevProps: any) {
    const {
      isAuthenticated,
      errorStatus,
      privileges,
      userId: userName,
    } = this.props.authentication;
    const passwordUpdateFlag = this.props.authentication.passwordUpdate;
    if (prevProps.authentication.passwordUpdate && !passwordUpdateFlag) {
      this.props.history.push(this.state.prePathname);
    }
    if ((errorStatus === '400' || errorStatus === '401') && prevProps.authentication.errorStatus !== errorStatus) {
      this.props.getSecurityPolicy();
    }
    if ((errorStatus === '487' || errorStatus === '488') && prevProps.authentication.errorStatus !== errorStatus) {
      this.props.doFetchLoginMessage();
      this.props.history.push({
        pathname: Login,
        password: this.state.newpassword,
        user: this.state.userFromLogin,
      });
    }
    if (isAuthenticated) {
      const {
        externalDashboardIP,
        logiOAuthClientId,
        logiOAuthClientSecret,
        logiOAuthClientName,
        logiAdminAccount,
        logiAdminUserName,
        logiAdminPswd,
        logiSupervisorUserName,
        logiSupervisorPswd,
      } = await this.props.getDXSettingsAndReturn();

      this.props.authenticateLogiUser({
        user: userName,
        password: this.state.confirmpassword,
        privileges,
        externalDashboardIP,
        logiOAuthClientId,
        logiOAuthClientSecret,
        logiOAuthClientName,
        logiAdminAccount,
        logiAdminUserName,
        logiAdminPswd,
        logiSupervisorUserName,
        logiSupervisorPswd,
      });
    }
  }

  componentWillUnmount() {
    this.props.undoSetPasswordUpdateFlag();
  }

  handleChange = (e: any) => {
    this.setState({ [e.target.name]: e.target.value });
  }

  handleSubmit = (e: any) => {
    e.preventDefault();
    this.props.clearErrorStatus();
    this.setState({ error: false, passwordPolicy: '' });
    if (this.state.newpassword !== this.state.confirmpassword) {
      this.setState({
        error: true,
        password: '',
        newpassword: '',
        confirmpassword: '',
      });
    } else {
      const { doLogin, clearPasswordPolicy } = this.props;
      const { newpassword, password, userFromLogin } = this.state;

      clearPasswordPolicy();

      const updatePasswordBody: WithAuditDetails<UpdatePasswordInterface> = {
        data: {
          action: 'password',
          newPassword: newpassword,
          password,
          user: userFromLogin,
        },
      };

      doLogin(updatePasswordBody);
    }
  }

  handleCancel = (e: any) => {
    e.preventDefault();
    this.setState({ error: false, passwordPolicy: '' });
    this.props.undoSetPasswordUpdateFlag();
    this.props.clearErrorStatus();
    this.props.clearPasswordPolicy();
    if (this.props.authentication.isAuthenticated) {
      this.props.history.push(this.state.prePathname);
    } else {
      this.props.history.push(Login);
    }
  }

  onKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      this.handleSubmit(e);
    }
  }

  render() {
    const { t } = this.props;
    const { error } = this.state;
    const { isAuthenticated, errorStatus, passwordUpdate } = this.props.authentication;
    // Only reachable from the user menu or the expired-password login flow, never by direct URL
    if (!isAuthenticated && !passwordUpdate) {
      return <Redirect to={Login} />;
    }
    return (
      <div className={isAuthenticated ? 'passwordupdate-authenticated-container' : 'passwordupdate-container'}>
        <div className="passwordupdate-form">
          <div className="passwordupdate-form-header">
            <img className="login-logo" src={ss8logo} alt={t('login.ss8Login')} />
            <h1>{t('login.xcipio')}</h1>
          </div>
          <h2>{t('login.updatePassword')}</h2>
          <form>
            <div className="bp3-input-group .modifier">
              <input
                type="password"
                name="password"
                className="bp3-input"
                placeholder={t('login.enterCurrentPassword')}
                onKeyDown={this.onKeyDown}
                value={this.state.password}
                onChange={this.handleChange}
              />

            </div>
            <div className="bp3-input-group .modifier">
              <input
                type="password"
                name="newpassword"
                className="bp3-input"
                placeholder={t('login.enterNewPassword')}
                onKeyDown={this.onKeyDown}
                value={this.state.newpassword}
                onChange={this.handleChange}
              />
            </div>
            <div className="bp3-input-group .modifier">
              <input
                type="password"
                name="confirmpassword"
                className="bp3-input"
                placeholder={t('login.enterNewPassword')}
                onKeyDown={this.onKeyDown}
                value={this.state.confirmpassword}
                onChange={this.handleChange}
              />
            </div>
            {error && (<div className="errorDisplay"><Icon intent="danger" icon="error" iconSize={18} /> {t('login.error.passwordDifferent')}</div>)}
            {errorStatus === '400' && (<div className="errorDisplay"><Icon intent="danger" icon="error" iconSize={18} /> {t('login.error.passwordPolicy')}</div>)}
            {errorStatus === '401' && passwordUpdate && (<div className="errorDisplay"><Icon intent="danger" icon="error" iconSize={18} />{t('login.error.wrongPassword')}</div>)}
            {this.state.passwordPolicy.length > 0 && (<div className="passwordPolicyDisplay">{this.state.passwordPolicy}</div>) }
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                className="bp3-button bp3-large bp3-icon-submit"
                onClick={this.handleCancel}
                value="submit"
              >{t('login.button.cancel')}
              </button> &nbsp; &nbsp;
              <button
                type="submit"
                className="bp3-button bp3-large bp3-icon-submit"
                onClick={this.handleSubmit}
                value="submit"
              >{t('login.changePassword')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }
}

const mapStateToProps = ({
  discoveryXcipio: {
    discoveryXcipioSettings: {
      Warrant_bAttachments_enabled: attachmentEnabled,
      isAuditEnabled,
      logiOAuthClientId,
      logiOAuthClientSecret,
      logiOAuthClientName,
      logiAdminAccount,
      logiAdminUserName,
      logiAdminPswd,
      logiSupervisorUserName,
      logiSupervisorPswd,
    },
  },
}: RootState) => ({
  attachmentEnabled,
  isAuditEnabled,
  logiOAuthClientId,
  logiOAuthClientSecret,
  logiOAuthClientName,
  logiAdminAccount,
  logiAdminUserName,
  logiAdminPswd,
  logiSupervisorUserName,
  logiSupervisorPswd,
});

const mapDispatchToProps = {
  getDXSettingsAndReturn,
};

export default withTranslation('translation', { withRef: true })(connect(mapStateToProps, mapDispatchToProps, null, {
  forwardRef: true,
})(PasswordUpdate));
